import React, { Component } from 'react'
import { Col, Descriptions, Form, FormInstance, InputNumber, Row, Select } from 'antd'
import { AgeProvider, AgeConsumer, AgeContextData } from '../../../../context/AgeContext'
import { SpecieProvider, SpecieConsumer, SpecieContextData } from '../../../../context/SpecieContext'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import MappingStepper, { Step } from '../MappingStepper'

type MappingAgeRangeFormProps = {
  form: FormInstance
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingAgeRangeFormInnerProps = MappingAgeRangeFormProps & {
  ageContext: AgeContextData
  specieContext: SpecieContextData
}

/**
 * Age range mappings are purely an Adopets concept (no partner / external side):
 * they bind an age + species to a numeric [min, max] range. So the wizard is just
 * "Our side" then "Review & Save".
 */
class MappingAgeRangeFormInner extends Component<MappingAgeRangeFormInnerProps> {
  componentDidMount() {
    const { ageContext, specieContext } = this.props

    if (!ageContext.fetching && !ageContext.ages?.length) {
      ageContext.requestAgeList({
        options: { limit: 9999 }
      })
    }

    if (!specieContext.fetching && !specieContext.species?.length) {
      specieContext.requestSpecieList({
        options: { limit: 9999 }
      })
    }
  }

  /** Resolve a species name for display, from the loaded species list. */
  specieName = (uuid?: string) =>
    this.props.specieContext.species?.find(s => s.uuid === uuid)?.name

  /** Resolve an age name for display, from the loaded age list. */
  ageName = (key?: string) =>
    this.props.ageContext.ages?.find(a => a.key === key)?.name

  // ----- Field groups -----

  renderAgeField = () => {
    const { ageContext } = this.props
    const agesOptions = ageContext.ages?.map(age => ({
      label: age.name,
      value: age.key,
    }))

    return (
      <Form.Item
        label="Age"
        name="age_key"
        rules={[{ required: true, message: 'Missing an age' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={ageContext.fetching}
          placeholder="Select an age"
          options={agesOptions}
        />
      </Form.Item>
    )
  }

  renderSpeciesField = () => {
    const { specieContext } = this.props
    const speciesOptions = specieContext.species?.map(specie => ({
      label: specie.name,
      value: specie.uuid,
    }))

    return (
      <Form.Item
        label="Species"
        name="specie_uuid"
        rules={[{ required: true, message: 'Missing a species' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={specieContext.fetching}
          placeholder="Select a species"
          options={speciesOptions}
        />
      </Form.Item>
    )
  }

  renderRangeField = () => (
    <Row gutter={12}>
      <Col xs={12}>
        <Form.Item
          label="Minimum"
          name="min"
          rules={[{ required: true, message: 'Missing a minimum' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Col>

      <Col xs={12}>
        <Form.Item
          label="Maximum"
          name="max"
          rules={[{ required: true, message: 'Missing a maximum' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>
  )

  // ----- Review -----

  renderReview = () => {
    const { form } = this.props

    const ageKey = form.getFieldValue('age_key')
    const specieUuid = form.getFieldValue('specie_uuid')
    const min = form.getFieldValue('min')
    const max = form.getFieldValue('max')

    const range = [min, max].every(v => v != null) ? `${min} – ${max}` : '—'

    return (
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Species">{this.specieName(specieUuid) || '—'}</Descriptions.Item>
        <Descriptions.Item label="Age">{this.ageName(ageKey) || '—'}</Descriptions.Item>
        <Descriptions.Item label="Range">{range}</Descriptions.Item>
      </Descriptions>
    )
  }

  // ----- Step definitions -----

  steps = (): Step[] => [
    {
      key: 'our-side',
      title: 'Our side',
      fields: ['age_key', 'specie_uuid', 'min', 'max'],
      content: () => (
        <>
          {this.renderSpeciesField()}
          {this.renderAgeField()}
          {this.renderRangeField()}
        </>
      ),
    },
    {
      key: 'review',
      title: 'Review & Save',
      fields: [],
      content: this.renderReview,
    },
  ]

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingAgeRangeForm extends Component<MappingAgeRangeFormProps> {
  render() {
    return (
      <SpecieProvider>
        <AgeProvider>
          <SpecieConsumer>
            {(specieContext) => (
              <AgeConsumer>
                {(ageContext) => (
                  <MappingAgeRangeFormInner
                    form={this.props.form}
                    loading={this.props.loading}
                    specieContext={specieContext}
                    ageContext={ageContext}
                  />
                )}
              </AgeConsumer>
            )}
          </SpecieConsumer>
        </AgeProvider>
      </SpecieProvider>
    )
  }
}
